export const DESIGN_PROJECT_ID = 'design';

const HANDLE_REF = { $ref: 'canvas-artifacts/agent/handle' };

const USAGE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['model', 'provider', 'input_tokens', 'output_tokens', 'total_tokens'],
  properties: {
    model: { type: 'string' },
    provider: { type: 'string' },
    input_tokens: { type: 'integer' },
    output_tokens: { type: 'integer' },
    total_tokens: { type: 'integer' },
    truncated_input: { type: 'boolean' },
  },
};

const agentInputParameters = {
  type: 'object',
  additionalProperties: false,
  required: ['source'],
  properties: {
    source: {
      type: 'string',
      title: 'Source',
      enum: ['file', 'inline', 'http', 'gcs'],
      default: 'inline',
    },
    path: {
      type: 'string',
      title: 'Local path',
      description:
        'file source only. Relative under DEPLOYMENTS_DIR (or workspace root). Absolute paths and ".." that escape the root are rejected.',
    },
    url: {
      type: 'string',
      title: 'HTTPS URL',
      description: 'http source only.',
    },
    gcs_uri: {
      type: 'string',
      title: 'GCS URI',
      description: 'gs://bucket/object — single object, no trailing-slash prefix.',
    },
    text: {
      type: 'string',
      title: 'Inline text',
      maxLength: 8192,
    },
    media_type: {
      type: 'string',
      title: 'Media Type',
      default: 'text/plain',
    },
    max_bytes: {
      type: 'integer',
      title: 'Max Bytes',
      default: 131072,
      maximum: 131072,
      description: '128 KiB. Matches think inline cap.',
    },
  },
};

const agentThinkParameters = {
  type: 'object',
  additionalProperties: false,
  required: ['prompt'],
  properties: {
    prompt: {
      type: 'string',
      title: 'Prompt',
      description:
        'Task for this node. Do not say "write a file" — the executor persists the reply.',
    },
    model: {
      type: 'string',
      title: 'Model',
      default: 'grok-4.5',
    },
    role: {
      type: 'string',
      title: 'Role',
      enum: ['qa', 'research', 'coding', 'general'],
      default: 'general',
      description: 'Pinned via pkg/run.Options.ForceIntent. Not ada.yaml.',
    },
    enable_diamond: {
      type: 'boolean',
      title: 'Enable Diamond',
      default: false,
      description: 'Registers run_diamond. Only usable when role is research or coding.',
    },
    keep_artifacts: {
      type: 'boolean',
      title: 'Keep Artifacts',
      default: true,
    },
    timeout_seconds: {
      type: 'integer',
      title: 'Timeout Seconds',
      default: 600,
      maximum: 1700,
    },
  },
};

const agentArtifactParameters = {
  type: 'object',
  additionalProperties: false,
  required: ['sink'],
  properties: {
    sink: {
      type: 'string',
      title: 'Sink',
      enum: ['file', 'json', 'gcs'],
      default: 'file',
    },
    filename: {
      type: 'string',
      title: 'Filename',
      default: 'artifact.md',
    },
    gcs_uri: {
      type: 'string',
      title: 'GCS URI',
      description: 'Single object URI.',
    },
    media_type: {
      type: 'string',
      title: 'Media Type',
    },
  },
};

const agentSinkParameters = {
  type: 'object',
  additionalProperties: false,
  properties: {
    note: {
      type: 'string',
      title: 'Note',
      description: 'Optional label; echoed into output_data.',
    },
  },
};

const agentInputPorts = {
  inputs: { type: 'object', properties: {} },
  outputs: {
    type: 'object',
    properties: { handle: HANDLE_REF },
  },
};

const agentThinkPorts = {
  inputs: {
    type: 'object',
    required: ['context'],
    properties: {
      context: HANDLE_REF,
      context_b: HANDLE_REF,
      context_c: HANDLE_REF,
    },
  },
  outputs: {
    type: 'object',
    required: ['thought', 'usage'],
    properties: {
      thought: HANDLE_REF,
      usage: USAGE_SCHEMA,
    },
  },
};

const agentArtifactPorts = {
  inputs: {
    type: 'object',
    required: ['data'],
    properties: { data: HANDLE_REF },
  },
  outputs: {
    type: 'object',
    properties: { handle: HANDLE_REF },
  },
};

const agentSinkPorts = {
  inputs: {
    type: 'object',
    required: ['data'],
    properties: { data: HANDLE_REF },
  },
  outputs: {
    type: 'object',
    required: ['ok'],
    properties: {
      ok: { type: 'boolean' },
      handle: HANDLE_REF,
    },
  },
};

const DESIGN_PKG_INBOX = 'design-pkg-inbox';
const DESIGN_PKG_EXTRACT = 'design-pkg-extract';
const DESIGN_PKG_LOOKUP = 'design-pkg-lookup';
const DESIGN_PKG_CATALOG = 'design-pkg-catalog';
const DESIGN_PKG_VAULT = 'design-pkg-vault';

const DESIGN_PRODUCED_AT = '2026-08-16T18:04:11Z';

const SAMPLE_INBOX_TEXT = [
  'From: GitHub <noreply@github.com>',
  'Subject: [container-labs/cloud] PR review requested',
  '',
  'From: Lenny\'s Newsletter <lenny@substack.com>',
  'Subject: How the best PMs write specs',
  'List-Unsubscribe: <https://lennysnewsletter.com/account>',
  '',
  'From: Stratechery <stratechery@substack.com>',
  'Subject: Aggregation Theory, 2026',
  'List-Unsubscribe: <https://stratechery.com/account>',
  '',
  'From: Amazon <auto-confirm@amazon.com>',
  'Subject: Your order has shipped',
  '',
  'From: The Batch <team@deeplearning.ai>',
  'Subject: Weekly ML digest (not Substack)',
  '',
  'From: Platformer <casey@substack.com>',
  'Subject: Why the app store fight is back',
  'List-Unsubscribe: <https://www.platformer.news/account>',
].join('\n');

const SAMPLE_EXTRACT_TEXT = [
  '3 Substack senders (skipped GitHub, Amazon, The Batch):',
  '',
  '1. Lenny\'s Newsletter — lennysnewsletter.com — lenny@substack.com',
  '2. Stratechery — stratechery.com — stratechery@substack.com',
  '3. Platformer — platformer.news — casey@substack.com',
].join('\n');

const SAMPLE_LOOKUP_TEXT = JSON.stringify(
  {
    publications: [
      {
        name: "Lenny's Newsletter",
        url: 'https://www.lennysnewsletter.com',
        author: 'Lenny Rachitsky',
        topics: ['product', 'growth'],
        subscribers_hint: '500k+',
      },
      {
        name: 'Stratechery',
        url: 'https://stratechery.com',
        author: 'Ben Thompson',
        topics: ['strategy', 'aggregation'],
        subscribers_hint: 'paid',
      },
      {
        name: 'Platformer',
        url: 'https://www.platformer.news',
        author: 'Casey Newton',
        topics: ['platforms', 'policy'],
        subscribers_hint: 'paid',
      },
    ],
  },
  null,
  2
);

/** Schema wants 64 hex; design mode is not a real digest. */
function designHandleDigest(text) {
  const src = String(text ?? '');
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5;
  for (let i = 0; i < src.length; i += 1) {
    const c = src.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193);
    h2 ^= c + i;
    h2 = Math.imul(h2, 16777619);
  }
  const a = (h1 >>> 0).toString(16).padStart(8, '0');
  const b = (h2 >>> 0).toString(16).padStart(8, '0');
  return `${a}${b}${a}${b}${a}${b}${a}${b}`.slice(0, 64);
}

function utf8ByteLength(text) {
  const value = String(text ?? '');
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  return value.length;
}

function makeInlineHandle({ producedBy, text, mediaType, preview, producedAt }) {
  const body = String(text ?? '');
  const clip = body.length > 200 ? `${body.slice(0, 199)}…` : body;
  return {
    kind: 'agent.handle.v1',
    scheme: 'inline',
    media_type: mediaType || 'text/plain',
    sha256: designHandleDigest(body),
    bytes: utf8ByteLength(body),
    preview: preview || clip,
    truncated: false,
    text: body,
    produced_by: producedBy,
    produced_at: producedAt || new Date().toISOString(),
  };
}

function packageFields(pkg) {
  if (!pkg) return { id: undefined, type: '', parameter_data: {} };
  if (pkg.parameter_data != null || pkg.type != null) {
    return {
      id: pkg.id,
      type: pkg.type || '',
      parameter_data: pkg.parameter_data || {},
    };
  }
  return {
    id: pkg.id,
    type: pkg.data?.type || '',
    parameter_data: pkg.data?.parameter_data || {},
  };
}

/**
 * Fabricate a completed v0 run. Design mode has no executor.
 */
export function fabricateDesignRunOutput(pkg, options = {}) {
  const { id, type, parameter_data: params } = packageFields(pkg);
  const producedAt = options.producedAt || new Date().toISOString();
  const upstream = options.upstreamOutputs || {};

  if (type === 'agent/input') {
    const text = params.text || SAMPLE_INBOX_TEXT;
    return {
      handle: makeInlineHandle({
        producedBy: id,
        text,
        mediaType: params.media_type || 'text/plain',
        producedAt,
      }),
    };
  }

  if (type === 'agent/think') {
    const lookup = id === DESIGN_PKG_LOOKUP;
    const text = lookup ? SAMPLE_LOOKUP_TEXT : SAMPLE_EXTRACT_TEXT;
    const preview = lookup
      ? '3 pubs: Lenny, Stratechery, Platformer'
      : '3 Substack senders (skipped noise)';
    return {
      thought: makeInlineHandle({
        producedBy: id,
        text,
        mediaType: lookup ? 'application/json' : 'text/markdown',
        preview,
        producedAt,
      }),
      usage: {
        model: params.model || 'grok-4.5',
        provider: 'xai',
        input_tokens: lookup ? 6100 : 3800,
        output_tokens: lookup ? 900 : 420,
        total_tokens: lookup ? 7000 : 4220,
        truncated_input: false,
      },
    };
  }

  if (type === 'agent/artifact') {
    return {
      handle: makeInlineHandle({
        producedBy: id,
        text: SAMPLE_LOOKUP_TEXT,
        mediaType: params.media_type || 'application/json',
        preview: 'substacks.json — 3 publications',
        producedAt,
      }),
    };
  }

  if (type === 'workflow/timer') {
    const label = params.label || params.cron || 'tick';
    return {
      tick: makeInlineHandle({
        producedBy: id,
        text: `tick ${label}`,
        mediaType: 'text/plain',
        preview: params.cron || label,
        producedAt,
      }),
    };
  }

  if (type === 'workflow/script') {
    const text = params.sample_output || 'script ok\nexit 0';
    return {
      result: makeInlineHandle({
        producedBy: id,
        text,
        mediaType: 'text/plain',
        preview: `${params.language || 'script'} exit 0`,
        producedAt,
      }),
    };
  }

  if (type === 'workflow/join') {
    const branches = ['branch_a', 'branch_b', 'branch_c']
      .map((key) => upstream[key])
      .filter(Boolean);
    const texts = branches.map((h) => h.text || h.preview || '');
    const reduce = params.reduce || 'concat';
    const text =
      reduce === 'json'
        ? JSON.stringify(
            Object.fromEntries(
              branches.map((h, i) => [`b${i}`, h.preview || h.text || ''])
            )
          )
        : texts.join('\n') || 'combined branches';
    return {
      combined: makeInlineHandle({
        producedBy: id,
        text,
        mediaType: reduce === 'json' ? 'application/json' : 'text/plain',
        preview: branches.length ? `${branches.length} branches` : 'joined',
        producedAt,
      }),
    };
  }

  if (type === 'workflow/placeholder') {
    const name = params.name || id || 'placeholder';
    const note = params.note ? `\n${params.note}` : '';
    return {
      resource: makeInlineHandle({
        producedBy: id,
        text: `provisioned ${name}${note}`,
        mediaType: 'text/plain',
        preview: name,
        producedAt,
      }),
    };
  }

  if (type === 'agent/sink') {
    const output = {
      ok: true,
      handle: makeInlineHandle({
        producedBy: id,
        text: SAMPLE_LOOKUP_TEXT,
        mediaType: 'application/json',
        preview: 'stored 3 Substacks',
        producedAt,
      }),
    };
    if (params.note) {
      output.note = params.note;
    }
    return output;
  }

  if (type === 'hetzner/network') {
    const netName = params.name || id || 'hetzner-net';
    return {
      network: {
        data: {
          id: String(Math.floor(Math.random() * 1e8)),
          name: netName,
          ip_range: params.ip_range || '10.0.0.0/16',
        },
      },
    };
  }

  if (type === 'hetzner/subnet') {
    const networkId = upstream?.network?.data?.id || String(Math.floor(Math.random() * 1e8));
    const ipRange = params.ip_range || '10.0.1.0/24';
    const zone = params.zone || 'us-east';
    return {
      subnet: {
        data: {
          id: `${networkId}/${ipRange}`,
          network_id: networkId,
          ip_range: ipRange,
          zone,
          type: 'server',
        },
      },
    };
  }

  if (type === 'hetzner/server') {
    const srvName = params.name || id || 'hetzner-server';
    const octet = Math.floor(Math.random() * 200 + 20);
    return {
      server: {
        data: {
          id: String(Math.floor(Math.random() * 1e9)),
          name: srvName,
          ipv4: `5.161.${octet}.${Math.floor(Math.random() * 250 + 2)}`,
          ipv6: '2a01:4ff:f0::/64',
          status: 'running',
          server_type: params.server_type || 'cpx11',
          location: params.location || 'ash',
        },
      },
    };
  }

  return {};
}

export function designDeployPatch(pkg, options = {}) {
  return {
    deploy_status: 'DEPLOYED',
    output_data: fabricateDesignRunOutput(pkg, options),
  };
}

export function designDestroyPatch() {
  return {
    deploy_status: 'NOT_DEPLOYED',
    output_data: {},
  };
}

/** Catalog entries for the sidebar (dragged onto the canvas as new nodes). */
export { PACKAGE_STUBS as designCatalogPackages } from './packageStubs';

const inboxParameterData = {
  source: 'inline',
  text: SAMPLE_INBOX_TEXT,
  media_type: 'text/plain',
};

const extractParameterData = {
  prompt:
    'From this mailbox dump, list only Substack newsletters. Ignore receipts, GitHub, and other senders. For each: title, host, from-address.',
  model: 'grok-4.5',
  role: 'general',
  enable_diamond: false,
};

const lookupParameterData = {
  prompt:
    'Look up each Substack publication. Return JSON: name, url, author, topics, subscribers_hint. Do not invent extra titles.',
  model: 'grok-4.5',
  role: 'research',
  enable_diamond: false,
};

const catalogParameterData = {
  sink: 'json',
  filename: 'substacks.json',
  media_type: 'application/json',
};

const vaultParameterData = {
  note: 'stored 3 Substack publications',
};

function seededPackage(pkg) {
  return {
    ...pkg,
    deploy_status: 'DEPLOYED',
    output_data: fabricateDesignRunOutput(pkg, { producedAt: DESIGN_PRODUCED_AT }),
  };
}

/** Inbox → extract Substacks → look up pubs → write JSON → store. */
export const designInitialProjectPackages = [
  seededPackage({
    id: DESIGN_PKG_INBOX,
    name: 'inbox',
    type: 'agent/input',
    project_id: DESIGN_PROJECT_ID,
    ...agentInputPorts,
    parameters: agentInputParameters,
    parameter_data: inboxParameterData,
  }),
  seededPackage({
    id: DESIGN_PKG_EXTRACT,
    name: 'extract-substacks',
    type: 'agent/think',
    project_id: DESIGN_PROJECT_ID,
    ...agentThinkPorts,
    parameters: agentThinkParameters,
    parameter_data: extractParameterData,
  }),
  seededPackage({
    id: DESIGN_PKG_LOOKUP,
    name: 'lookup-pubs',
    type: 'agent/think',
    project_id: DESIGN_PROJECT_ID,
    ...agentThinkPorts,
    parameters: agentThinkParameters,
    parameter_data: lookupParameterData,
  }),
  seededPackage({
    id: DESIGN_PKG_CATALOG,
    name: 'write-catalog',
    type: 'agent/artifact',
    project_id: DESIGN_PROJECT_ID,
    ...agentArtifactPorts,
    parameters: agentArtifactParameters,
    parameter_data: catalogParameterData,
  }),
  seededPackage({
    id: DESIGN_PKG_VAULT,
    name: 'store',
    type: 'agent/sink',
    project_id: DESIGN_PROJECT_ID,
    ...agentSinkPorts,
    parameters: agentSinkParameters,
    parameter_data: vaultParameterData,
  }),
];

export const designInitialConnections = [
  {
    id: 'design-conn-inbox-extract',
    project_id: DESIGN_PROJECT_ID,
    source_package_id: DESIGN_PKG_INBOX,
    target_package_id: DESIGN_PKG_EXTRACT,
    source_handle: 'handle',
    target_handle: 'context',
  },
  {
    id: 'design-conn-extract-lookup',
    project_id: DESIGN_PROJECT_ID,
    source_package_id: DESIGN_PKG_EXTRACT,
    target_package_id: DESIGN_PKG_LOOKUP,
    source_handle: 'thought',
    target_handle: 'context',
  },
  {
    id: 'design-conn-lookup-catalog',
    project_id: DESIGN_PROJECT_ID,
    source_package_id: DESIGN_PKG_LOOKUP,
    target_package_id: DESIGN_PKG_CATALOG,
    source_handle: 'thought',
    target_handle: 'data',
  },
  {
    id: 'design-conn-catalog-vault',
    project_id: DESIGN_PROJECT_ID,
    source_package_id: DESIGN_PKG_CATALOG,
    target_package_id: DESIGN_PKG_VAULT,
    source_handle: 'handle',
    target_handle: 'data',
  },
];
